/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import {
	Drawer,
	Box,
	ScrollArea,
	Button,
	Container,
	Title,
	Text,
	Group,
} from '@mantine/core';
import { useForm, isNotEmpty } from '@mantine/form';
import { randomId, useMediaQuery } from '@mantine/hooks';

// Styles
import classes from './DrawerViewDetails.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Store
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import { ViewDetails } from './ViewDetails';
import { ConfirmationModal } from '@components/molecules';
import { useRollbackImport } from '@features/sourcing/import-history/api/rollback-import';
import { queryClient } from '@lib/react-query';

// Icons
import { IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useProductions } from '@features/sourcing/import-history/api/get-productions';
import { isNil } from 'lodash';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerViewDetails = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** Media Query ******************************* */

	const isLargeScreen = useMediaQuery(
		'(min-width: 1920px)'
	);

	const isMediumScreen = useMediaQuery(
		'(min-width: 1440px) and (max-width: 1919px)'
	);

	const isSmallScreen = useMediaQuery(
		'(max-width: 1439px)'
	);

	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);

	const [isOpenModalRollback, setIsOpenModalRollback] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		productionsAddType,
		importHistorySelected,
		importHistoriesPage,
		importHistoriesPageSize,
		importHistoriesQueryParams,
		setImportHistoriesLinesQueryParams,
		enableRollback,
		setEnableRollback,
		importHistoriesLinesQueryParams,
		importHistoriesLinesSortParams,
	} = useStore();

	const {
		data: productions,
		isLoading: isLoadingProductions,
		importHasVerifiedOrMintedProduction,
	} = useProductions(
		importHistoriesLinesQueryParams,
		importHistoriesLinesSortParams,
		importHistorySelected.id,
		importHistorySelected.lines &&
			Array.isArray(importHistorySelected.lines)
			? importHistorySelected.lines.length
			: 0
	);

	const { t } = useTranslation();

	const IS_FROM_FILE_ADD_PRODUCTION =
		productionsAddType !== 'manually';

	// Validators
	const form = useForm({
		initialValues: {
			name: '',
			brand: '',
			tag: '',
			quantity: 0,
			startDate: null,
			endDate: null,
			producer: null,
			project: null,
			site: null,
			meterPoint: null,
			unitProduction: null,
			productions: [
				{
					id: randomId(),
					quantity: 0,
					startDate: null,
				},
			],
		},
		validate: {
			producer: isNotEmpty(
				t('errors.producerIsRequired')
			),
			project: isNotEmpty(t('errors.projectIsRequired')),
			site: isNotEmpty(t('errors.siteIsRequired')),
			meterPoint: isNotEmpty(
				t('errors.meterpointIsRequired')
			),
			unitProduction: isNotEmpty(
				t('errors.meterpointIsRequired')
			),
			name: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value.length < 2
					? t('errors.nameMustHaveAtLeast2Letters')
					: null,
			brand: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value.length == 0
					? t('errors.brandIsRequired')
					: null,
			tag: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value.length == 0
					? t('errors.tagIsRequired')
					: null,

			quantity: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value == 0
					? t('errors.quantityCannotBeZero')
					: null,
			startDate: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value == null
					? t('errors.startDateIsRequired')
					: null,

			endDate: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value == null
					? t('errors.endDateIsRequired')
					: null,

			productions: {
				quantity: (value) =>
					!IS_FROM_FILE_ADD_PRODUCTION && value == 0
						? t('errors.quantityCannotBeZero')
						: null,
				startDate: (value) =>
					!IS_FROM_FILE_ADD_PRODUCTION && value == null
						? t('errors.startDateIsRequired')
						: null,
			},
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	const { isSuccess, isError } = useRollbackImport({
		importId: importHistorySelected.id
			? importHistorySelected.id
			: '',
		queryConfig: {
			enabled: enableRollback,
		},
	});

	useEffect(() => {
		if (isSuccess) {
			setEnableRollback(false);
			queryClient.invalidateQueries({
				queryKey: [
					'import-histories',
					{
						page: importHistoriesPage,
						size: importHistoriesPageSize,
						queryParams: importHistoriesQueryParams,
					},
				],
			});
			notifications.show({
				title: t('global.success'),
				message: t(
					`importHistories.notifications.successImport`
				),
				color: 'success',
			});
		}
	}, [isSuccess]);

	useEffect(() => {
		if (isError) {
			setEnableRollback(false);
			notifications.show({
				title: t('global.error'),
				message: t(`api.ROLLBACK_IMPORT`),
				color: 'error',
			});
		}
	}, [isError]);

	const getContainerSize = () => {
		if (isLargeScreen) {
			return 'lg';
		}
		if (isMediumScreen) {
			return 'md';
		}
		if (isSmallScreen) {
			return 'sm';
		}
		return 'xl';
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<>
			<Drawer.Root
				position="right"
				size="100%"
				opened={props.opened}
				onClose={() => {
					form.setFieldValue('producer', null);
				}}
			>
				<Drawer.Overlay />
				<Drawer.Content
					ref={viewport}
					className={classes.root}
				>
					{/**************************** Delete confirmation modal ****************************/}

					<ConfirmationModal
						title={t(
							'importHistories.modal.rollback.title'
						)}
						description={t(
							'importHistories.modal.rollback.description'
						)}
						onConfirm={() => {
							if (importHistorySelected.id) {
								setEnableRollback(true);
							}

							setIsOpenModalRollback(false);
							props.onClose();
							setImportHistoriesLinesQueryParams({});
						}}
						onClose={() => {
							setIsOpenModalRollback(false);
						}}
						opened={isOpenModalRollback}
					/>
					<DefaultModal
						opened={isOpenModalCancel}
						onConfirm={() => {
							props.onClose();
							setIsOpenModalCancel(false);

							form.setFieldValue('producer', null);
						}}
						onClose={() => {
							setIsOpenModalCancel(false);
						}}
						title={t('global.backToPreviousSure')}
					/>
					{/** Header */}
					<Drawer.Header className={classes.DrawerHeader}>
						<Box className={classes.header}>
							<PreviousDrawerButton
								onClick={() => {
									props.onClose();
								}}
							>
								{t(
									'importHistories.backToAllImportHistories'
								)}
							</PreviousDrawerButton>{' '}
						</Box>
					</Drawer.Header>
					{/** Body */}
					<Drawer.Body className={classes.body}>
						<Container
							id="something-special"
							p={0}
							px={0}
							size={getContainerSize()}
						>
							{/** Title */}
							<Box className={classes.containerTitle}>
								<Box
									className={classes.contentTitleEmpty}
								/>
								<Box className={classes.contentTitle}>
									<Title className={classes.titleForm}>
										{t('importHistories.title')}
									</Title>
									<Text className={classes.subTitleForm}>
										/ {t('productions.title')}
									</Text>
								</Box>
								<Box
									className={classes.contentTitleEmpty}
								/>
							</Box>

							<Box className={classes.bodyContent}>
								{/** Content */}
								<ScrollArea
									viewportRef={viewport}
									className={classes.containerContent}
								>
									<ViewDetails
										form={form}
										onCancel={() => {}}
										onBack={() => {}}
										data={productions}
										isLoading={isLoadingProductions}
									/>
								</ScrollArea>
							</Box>
						</Container>
					</Drawer.Body>
					{/** Footer */}
					<Box className={classes.containerFooter}>
						<Box className={classes.spacerFooter} />
						<Box className={classes.bodyFooter}>
							<Group grow wrap="nowrap">
								<Button
									loading={isNil(
										importHasVerifiedOrMintedProduction
									)}
									disabled={
										!isNil(
											importHasVerifiedOrMintedProduction
										)
											? importHasVerifiedOrMintedProduction
											: false
									}
									color="error"
									size="md"
									className={classes.customRollBackButton}
									onClick={() => {
										setIsOpenModalRollback(true);
									}}
									leftSection={<IconTrash size={19} />}
								>
									{t('global.rollbackImport')}
								</Button>

								<Button
									size="md"
									onClick={() => {
										props.onClose();
									}}
									className={classes.customSubmitButton}
								>
									{t('global.ok')}
								</Button>
							</Group>
						</Box>

						<Box className={classes.spacerFooter} />
					</Box>
				</Drawer.Content>
			</Drawer.Root>
		</>
	);
};
